<template>
  <Popper
    ref="popper"
    v-slot="{
      popperId,
      isShown,
      shouldMountContent,
      skipTransition,
      autoHide,
      hide,
      handleResize,
      onResize,
      classes,
      result,
    }"
    v-bind="{ ...$attrs, 'auto-hide': autoHideComputed }"
    :theme="theme"
    :target-nodes="getTargetNodes"
    :reference-node="() => $refs.reference"
    :popper-node="() => $refs.popperContent.$el"
  >
    <div
      ref="reference"
      class="v-popper"
      :class="[
        themeClass,
        {
          'v-popper--shown': isShown,
        },
      ]"
    >
      <slot />

      <PopperContent
        ref="popperContent"
        :popper-id="popperId"
        :theme="theme"
        :shown="isShown"
        :mounted="shouldMountContent"
        :skip-transition="skipTransition"
        :auto-hide="autoHide"
        :handle-resize="handleResize"
        :classes="classes"
        :class="{ 'v-popper__popper--modal-on-top': modalsOnTop.size > 0 }"
        :result="result"
        data-test-id="popover"
        @hide="hide"
        @resize="onResize"
      >
        <slot name="popper" :shown="isShown" />
      </PopperContent>
    </div>
  </Popper>
</template>

<script lang="ts">
/* eslint-disable new-cap */
import { defineComponent } from 'vue';
import { Popper, PopperContent, PopperMethods, ThemeClass } from 'floating-vue';
import { modalTransitionDurationMs } from '@sass/globals.module.scss';

const modalTransitionDuration = Number(modalTransitionDurationMs);

/**
 * Custom version of floating-vue VDropdown based on
 * https://floating-vue.starpad.dev/guide/custom-component with some fixes from
 * https://github.com/Akryum/floating-vue/blob/e3ffe0017169ad51ae94597ec60354257b3b7eda/packages/floating-vue/src/components/PopperWrapper.vue
 *
 * It integrates with the app's modal stack:
 *
 * When a modal is opened:
 *
 * - autoHide is disabled (if defined), so the popover is kept open.
 * For example, when the user is opening a modal directly from a dropdown, or when a dropdown
 * is open and the user opens a modal with a keyboard shortcut.
 *
 * - z-index of the popover is reduced
 */
export default defineComponent({
  name: 'ADropdown',

  components: {
    Popper: Popper(),
    PopperContent,
  },

  inheritAttrs: false,

  mixins: [PopperMethods, ThemeClass()],

  props: {
    theme: {
      type: String,
      default: 'atlasti-dropdown',
    },
  },

  data(): {
    modalsOnTop: Set<string>;
    unsubscribe: () => void;
  } {
    return {
      modalsOnTop: new Set(),
      unsubscribe: () => {},
    };
  },

  methods: {
    getTargetNodes() {
      return Array.from((this.$refs.reference as Element).children).filter(
        (el) => el !== (this.$refs.popperContent as InstanceType<typeof PopperContent>).$el
      );
    },
  },

  computed: {
    autoHideComputed() {
      if (this.modalsOnTop.size) return false;

      return this.$attrs['auto-hide'];
    },
  },

  created() {
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'modal/MODAL_SHOW') {
        this.modalsOnTop.add(mutation.payload.type);
      } else if (mutation.type === 'modal/MODAL_HIDE') {
        // delay so that modal backdrop transition can finish
        // and popper element doesn't "pop" on top of the background immediately
        setTimeout(() => {
          this.modalsOnTop.delete(mutation.payload.type);
        }, modalTransitionDuration);
      }
    });
  },

  unmounted() {
    this.unsubscribe();
  },
});
</script>
<style lang="scss">
@use 'settings/settings';

.v-popper.v-popper--theme-atlasti-dropdown {
  display: flex;
}

.v-popper__popper.v-popper--theme-atlasti-dropdown,
.v-popper__popper.v-popper--theme-plain {
  z-index: settings.$tooltip-z-index;

  &.v-popper__popper--modal-on-top {
    z-index: settings.$modalZIndex - 1;
  }

  .v-popper__arrow-container {
    display: none;
  }
}
</style>
